/**
 * Sign-up form rules (Sprint 20, spec 2.1/2.2) - PURE. The function
 * re-checks everything; this is the client's first pass so the form can say
 * what is wrong before the round trip. Payload shapes are contract 1.2/1.3.
 * No contract at sign-up (owner's rule, 2026-10-28): the requirement is
 * derived from age (contractRule.js), so every athlete is sent with
 * contractMinutes null and the contract screen stamps it on first open.
 */
import { ALL_PACKAGES, ELITE, packageById } from './packages';
import { householdFacility } from './facility';
import { researchAnswer } from './researchConsent';

export const EMAIL_RE = /^\S+@\S+\.\S+$/;
export const ADULT_AGE = 18;
export const HANDICAP_MIN = 0;
export const HANDICAP_MAX = 54;
/** The one wording of that range: sign-up's form and the academy's handicap edit (hooks/handicap.js) both say it. */
export const HANDICAP_RANGE_ERROR = 'Handicap is a whole number from 0 to 54, or leave it blank.';
/** Spec 13 cut line: flip to false to hide the own-login toggle if claimInvite slips. */
export const CHILD_LOGIN_ENABLED = true;
export const U13_HELPER =
  'Under 13? A Google account needs Family Link permission for third-party sign-in; a new password login works either way.';
export const ADULT_REQUIRED = 'Student sign-up is 18+. A parent or guardian needs to complete this for you.';
const DOB_REQUIRED = 'Date of birth is required — it determines U13 vs U18 eligibility.';

let seq = 0;
export function newAthleteEntry() {
  seq += 1;
  return {
    key: `new-${seq}`, name: '', dob: '', packageId: null, handicap: '', ownLogin: false, loginEmail: '',
    // Assessments spec 7: the research opt-in and, under 18, the assent box.
    research: false, researchAssent: false,
  };
}

/**
 * The form's family facility tick (`form.facilityRequested`) as a saved draft
 * restores it. The tick was once per athlete: a draft from that form (still
 * draft v1, no family field) restores ticked when any of its athletes was;
 * one from before the add-on existed restores unticked. Once the family
 * field is there it alone decides - the old per-athlete ticks stay behind on
 * the entries, unread.
 */
export function restoredFacilityTick(form) {
  if (!form) return false;
  if (typeof form.facilityRequested === 'boolean') return form.facilityRequested;
  return Array.isArray(form.athletes) && form.athletes.some((a) => a?.facilityRequested === true);
}

/**
 * The $300/month facility add-on, ticked under the package cards (owner
 * request, Mike 2026-09-30). 'offer' on the monthly token packages;
 * 'included' for Elite (24/7 access is part of it); null for the one-time
 * single token, which never has it, and for no pick yet. A tick is a request,
 * never a charge: the add-on's checkout opens once the membership is paid
 * (checkout.js refuses 'billing-not-active').
 */
export function facilityOptionFor(packageId) {
  const kind = packageId == null ? null : packageById(packageId)?.kind;
  if (kind === 'tokens') return 'offer';
  return kind === 'elite' ? 'included' : null;
}

/**
 * It is a FAMILY add-on (owner ruling 2026-09-30): one tick for the whole
 * form, offered once, never once per child. 'included' when any athlete in
 * the form is on Elite (Elite covers the family); 'offer' when someone is on
 * a 4, 6, 8, 12 or 16 token package; null otherwise. `existing` (link mode) is the
 * athletes already in the family: a live Elite membership there includes it
 * too, and anything that makes addAthletes store no request - an Elite
 * athlete still to pay, a request already made, an add-on already paid -
 * means it is not offered again. null while unknown: addAthletes decides.
 */
export function familyFacilityOption(athletes, existing = null) {
  const options = (athletes || []).map((a) => facilityOptionFor(a?.packageId));
  const there = (existing || []).filter(Boolean);
  const family = householdFacility(there);
  if (options.includes('included') || family.source === 'elite') return 'included';
  if (!options.includes('offer')) return null;
  const blocked = family.access || there.some((a) => a.facilityRequested === true || (a.packageId === ELITE.id && a.billing?.status !== 'lapsed'));
  return blocked ? null : 'offer';
}

/** The family tick that still counts: one left on before an Elite pick, or with nobody on a token package, means nothing (createFamily stores false too). */
export function wantsFacility(form) {
  return Boolean(form) && form.facilityRequested === true && familyFacilityOption(form.athletes) === 'offer';
}

/** Which athlete the request is sent on - the add-on bills on one athlete: the first with a 4, 6, 8, 12 or 16 token package. -1 == nobody. */
export function facilityHolderIndex(form) {
  return wantsFacility(form) ? form.athletes.findIndex((a) => facilityOptionFor(a?.packageId) === 'offer') : -1;
}

/** The consent step's facility waiver turns required once the family keeps the add-on. */
export function facilityWaiverRequired(form) {
  return wantsFacility(form);
}
export const FACILITY_WAIVER_FOOTNOTE = 'Needed for the facility access you picked';
export const FACILITY_WAIVER_REQUIRED =
  'Tick the facility access waiver to keep the add-on, or untick facility access on the package step.';

/** 'Nico', 'Nico and Reese', 'Nico, Reese and Sam'. */
export function joinNames(names) {
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0] || '';
}

/**
 * Emergency contact (owner, 2026-09-30: its own mobile and relationship
 * fields). Optional as a block; once any field is filled, name and mobile are
 * both required. Drafts saved before the split hold one string, which becomes
 * the name.
 */
export function emptyEmergencyContact() {
  return { name: '', phone: '', relationship: '' };
}
export function toEmergencyForm(v) {
  if (typeof v === 'string') return { ...emptyEmergencyContact(), name: v };
  if (!v || typeof v !== 'object') return emptyEmergencyContact();
  const text = (x) => (typeof x === 'string' ? x : '');
  return { name: text(v.name), phone: text(v.phone), relationship: text(v.relationship) };
}
export function validateEmergencyContact(ec) {
  const e = toEmergencyForm(ec);
  const blank = (s) => s.trim() === '';
  if (blank(e.name) && blank(e.phone) && blank(e.relationship)) return {};
  const errors = {};
  if (blank(e.name)) errors.name = 'Add their name, or clear the other emergency fields.';
  if (blank(e.phone)) errors.phone = 'Add a mobile number we can call.';
  return errors;
}
/** Contract 1.2/1.3 `emergencyContact`: trimmed, or null when all three are blank. */
export function emergencyBody(ec) {
  const e = toEmergencyForm(ec);
  const [name, phone, relationship] = [e.name.trim(), e.phone.trim(), e.relationship.trim()];
  return name || phone || relationship ? { name, phone, relationship: relationship || null } : null;
}

/** Whole years old on `todayISO`; null for anything but 'yyyy-MM-dd'. No Date.now(). */
export function ageOnDate(dob, todayISO) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dob || '') || !/^\d{4}-\d{2}-\d{2}$/.test(todayISO || '')) return null;
  const [y, m, d] = dob.split('-').map(Number);
  const [ty, tm, td] = todayISO.split('-').map(Number);
  let age = ty - y;
  if (tm < m || (tm === m && td < d)) age -= 1;
  return age;
}

export function isAdultOnDate(dob, todayISO) {
  const age = ageOnDate(dob, todayISO);
  return age != null && age >= ADULT_AGE;
}

/** '' -> null; an integer 0..54 -> itself; anything else -> undefined (invalid). */
export function normalizeHandicap(raw) {
  if (raw === '' || raw == null) return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= HANDICAP_MIN && n <= HANDICAP_MAX && String(raw).trim() !== '' ? n : undefined;
}

const lower = (s) => (s || '').trim().toLowerCase();

export function validateAthleteEntry(a, { todayISO, guardianEmail = '', siblings = [], mode = 'parent' } = {}) {
  const errors = {};
  if (!a.name || a.name.trim() === '') errors.name = 'Athlete name is required.';
  const age = ageOnDate(a.dob, todayISO);
  if (age == null) errors.dob = DOB_REQUIRED;
  else if (a.dob > todayISO) errors.dob = 'That date is in the future - check the year.';
  else if (mode === 'athlete' && age < ADULT_AGE) errors.dob = ADULT_REQUIRED;
  if (a.packageId != null && !ALL_PACKAGES.some((p) => p.id === a.packageId)) errors.packageId = 'Pick a package from the list.';
  if (normalizeHandicap(a.handicap) === undefined) errors.handicap = HANDICAP_RANGE_ERROR;
  // Own login is a parent-mode choice (the adult athlete IS the login); a
  // toggle left on before switching to athlete mode is ignored, as the
  // payload builder and createFamily ignore it.
  if (mode === 'parent' && a.ownLogin) {
    const email = lower(a.loginEmail);
    if (!EMAIL_RE.test(email)) errors.loginEmail = 'Enter the email the athlete will sign in with.';
    else if (email === lower(guardianEmail)) errors.loginEmail = "Use a different email from the guardian's.";
    else if (siblings.some((s) => s !== a && s.ownLogin && lower(s.loginEmail) === email)) errors.loginEmail = 'Each athlete needs their own email.';
  }
  return errors;
}

function athleteBody(a, facilityRequested) {
  return {
    name: a.name.trim(),
    dob: a.dob,
    packageId: a.packageId,
    // Null for everyone, even a goal a restored draft still holds: the
    // requirement is derived, and stamped from the contract screen.
    contractMinutes: null,
    handicap: normalizeHandicap(a.handicap) ?? null,
    loginEmail: a.ownLogin && a.loginEmail ? lower(a.loginEmail) : null,
    facilityRequested,
  };
}

/** Every athlete's body; the family facility tick rides on exactly one of them (facilityHolderIndex). */
function athleteBodies(form) {
  const holder = facilityHolderIndex(form);
  return form.athletes.map((a, i) => athleteBody(a, i === holder));
}

/**
 * Contract 1.2 request body. Athlete mode: no relationship, no child login
 * (the caller IS the login). Each athlete carries its research answer
 * (assessments spec 7), under 18 by `todayISO`; createFamily re-checks it.
 */
export function buildCreateFamilyPayload(form, { todayISO } = {}) {
  const parent = form.mode === 'parent';
  const researchOf = (a) => researchAnswer({ ticked: a.research === true, assent: a.researchAssent === true, minor: parent && !isAdultOnDate(a.dob, todayISO) });
  return {
    mode: form.mode,
    contact: {
      name: form.contact.name.trim(),
      email: form.contact.email.trim(),
      phone: form.contact.phone.trim(),
      relationship: parent ? form.contact.relationship || null : null,
    },
    athletes: athleteBodies(form).map((a, i) => ({ ...(parent ? a : { ...a, loginEmail: null }), research: researchOf(form.athletes[i]) })),
    emergencyContact: emergencyBody(form.emergencyContact),
    medical: form.medical.trim() || null,
    consents: {
      dataCollection: Boolean(form.consents.dataCollection),
      videoCapture: Boolean(form.consents.videoCapture),
      mediaRelease: Boolean(form.consents.mediaRelease),
      facilityAccess: Boolean(form.consents.facilityAccess),
    },
    signatureName: form.signatureName.trim(),
  };
}

/** Contract 1.3 request body (Settings' "Link another athlete"). A null contact means "use the household's". */
export function buildAddAthletesPayload(form) {
  return {
    athletes: athleteBodies(form),
    emergencyContact: emergencyBody(form.emergencyContact),
    medical: form.medical.trim() || null,
  };
}
